import { Injectable } from '@angular/core';
import { Suffixes } from '../swagger-api';
import FieldsConstants from './fields.constants';

@Injectable()
export default class AppValues {
  public static valueMB = 1000000;
  public static BYTE_IN_GB = 1073741824;

  public static warningImage = 'assets/icons/icon-warning.svg';

  public static alphabeticLowCasePattern = new RegExp(/^[a-z]+$/);
  public static aliasPattern = new RegExp(/^[\x21-\x7E\s]+$/);
  public static organizationNamePattern = new RegExp(/^[\x21-\x7E\s]+$/);
  public static alphabeticPattern = new RegExp(/^[a-zA-Z-'. ]+$/);
  public static alphabeticNamePattern = new RegExp(/^[a-zA-Z-' ]+$/);
  public static alphanumericPattern = new RegExp(/^[a-zA-Z0-9-']+$/);
  public static whiteSpacesOnlyPattern = new RegExp(/^$|.*\S+.*/);

  public static refreshChatTokenInterval = 30 * 60 * 1000; // 30 minutes

  // RFC 2821, 2822 Compliant
  public static emailPattern = new RegExp(
    /^((([!#$%&'*+\-/=?^_`{|}~\w])|([!#$%&'*+\-/=?^_`{|}~\w][!#$%&'*+\-/=?^_`{|}~.\w]{0,}[!#$%&'*+\-/=?^_`{|}~\w]))[@]\w+([-.]\w+)*\.\w+([-.]\w+)*)$/i,
  );

  public static datePattern = new RegExp(/^(0?[1-9]|1[0-2])[-./](0?[1-9]|[12][0-9]|3[01])[-./]\d{4}$/);
  public static numericPattern = new RegExp(/^[0-9]+$/);
  public static numberPattern = new RegExp(/\d/);
  public static noSymbolsPattern = new RegExp(/^\+?\d{1,15}$/);

  public static lettersPattern = new RegExp(/(?=.*[a-z])(?=.*[A-Z])/);
  public static lettersPatternAnyRegister = new RegExp(/(?=.*[a-zA-Z])/);
  public static symbolsPattern = new RegExp(/[!@#$%^&*()[\]{}|?+\-',./:;<=>_`~"]/);
  public static whitespacesPattern = new RegExp(/\s/);
  public static repeatedCharactersPattern = new RegExp(/^(?!.*(.)\1{2,}).+$/);
  public static zipCodePattern = new RegExp(/^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/);
  public static urlPattern = new RegExp(/^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$/i);
  public static httpPattern = new RegExp(/^((https|http):\/\/)/i);

  /**
   * @description Pattern for URLs starting with http://, https://, or ftp://
   */
  public static replacePatternWeb = new RegExp(/(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim);

  /**
   * @description Pattern for URLs starting with "www." (without // before it, or it'd re-link the ones done above).
   */
  public static replacePatternWebHaveWww = new RegExp(/(^|[^/])(www.[\S]+(\b|$))/gim);

  /**
   * @description Pattern for change email addresses to mailto:: links
   */
  public static replacePatternMailTo = new RegExp(/(([a-zA-Z0-9-_.])+@[a-zA-Z_]+?(.[a-zA-Z]{2,6})+)/gim);
  public static usernameReplyCommentPattern = /@(.*?),/;
  public static excludeImgTagsPattern = /<img[^>]*>/g;
  public static yesNoSelectValues: string[] = ['YES', 'NO'];
  public static get postfixSuffixes(): string[] {
    return [Suffixes.Sr, Suffixes.Jr, Suffixes.PhD, Suffixes.Esq];
  }

  public static get prefixSuffixes(): string[] {
    return [Suffixes.Mr, Suffixes.Ms, Suffixes.Mrs, Suffixes.Dr];
  }

  public static get suffixes(): string[] {
    return [...this.prefixSuffixes, ...this.postfixSuffixes, Suffixes.Empty];
  }

  public static readonly DATE_TIME_FORMAT_LONG: string = 'MMM d, yyyy hh:mm a';
  public static readonly DATE_TIME_FORMAT_SHORT: string = 'MM/dd/yyyy hh:mm a';
  public static readonly DATE_FORMAT_SHORT: string = 'MM/dd/yyyy';
  public static readonly DATE_FORMAT_LONG: string = 'MMM d, y';

  public static genderTypes: string[] = ['Male', 'Female', 'Transman', 'Transwoman', 'Non-binary', 'Agender', 'Other'];
  public static languages: string[] = [
    'Mandarin',
    'Spanish',
    'English',
    'Hindi',
    'Bengali',
    'Portuguese',
    'Russian',
    'Japanese',
    'Western Punjabi',
    'Marathi',
    'Telugu',
    'Wu Chinese',
    'Turkish',
    'Korean',
    'French',
    'German',
    'Vietnamese',
    'Tamil',
    'Yue Chinese',
    'Urdu',
    'Javanese',
    'Italian',
    'Egyptian Arabic',
    'Gujarati',
    'Iranian Persian',
    'Bhojpuri',
    'Min Nan Chinese',
    'Hakka Chinese',
    'Jin Chinese',
    'Hausa',
    'Kannada',
    'Indonesian (Indonesian Malay)',
    'Polish',
    'Yoruba',
    'Xiang Chinese',
    'Malayalam',
    'Odia',
    'Maithili',
    'Burmese',
    'Eastern Punjabi',
    'Sunda',
    'Sudanese Arabic',
    'Algerian Arabic',
    'Moroccan Arabic',
    'Ukrainian',
    'Igbo',
    'Northern Uzbek',
    'Sindhi',
    'North Levantine Arabic',
    'Romanian',
    'Tagalog',
    'Dutch',
    'Saʽidi Arabic',
    'Gan Chinese',
    'Amharic',
    'Northern Pashto',
    'Magahi',
    'Thai',
    'Saraiki',
    'Khmer',
    'Chhattisgarhi',
    'Somali',
    'Malay (Malaysian Malay)',
    'Cebuano',
    'Nepali',
    'Mesopotamian Arabic',
    'Assamese',
    'Sinhala',
    'Northern Kurdish',
    'Hejazi Arabic',
    'Nigerian Fulfulde',
    'Bavarian',
    'South Azerbaijani',
    'Greek',
    'Chittagonian',
    'Kazakh',
    'Deccan',
    'Hungarian',
    'Kinyarwanda',
    'Zulu',
    'South Levantine Arabic',
    'Tunisian Arabic',
    'Sanaani Spoken Arabic',
    'Min Bei Chinese',
    'Southern Pashto',
    'Rundi',
    'Czech',
    'Taʽizzi-Adeni Arabic',
    'Uyghur',
    'Min Dong Chinese',
    'Sylheti',
  ];

  public static emptyCharacter = '&nbsp;';

  public static reactivationTab = 'reactivation';

  public static readonly HIGHLIGHTED_CLASS: string = 'highlighted';
  public static readonly SCROLL_BLOCK_POSITION: ScrollLogicalPosition = 'center';
  public static readonly SCROLL_BEHAVIOR: ScrollBehavior = 'smooth';
  public static readonly HIGHLIGHTED_TIME: number = 2000;

  public static readonly DEBOUNCE_TIME: number = 200;

  public static readonly PAGE_LIMIT: number = 10;
  public static readonly ACTIVITIES_PAGE_OFFSET: number = 0;

  public static readonly CROPPER_WIDTH: number = 180;
  public static readonly CROPPER_HEIGHT: number = 180;

  public static readonly DEACTIVATION_PAGE_SIZE = 10;

  public static readonly ADMINS_PAGE_SIZE = 10;

  public static readonly PERMISSION_READER: string = 'Reader';
  public static readonly PERMISSION_OWNER: string = 'Owner';

  public static readonly COMMENT_QUOTE_LENGTH: number = 30;

  public static readonly COMMENT_MAX_LENGTH: number = FieldsConstants.MAX_COMMENT_LENGTH;

  public static KEYCODE_ENTER = 'Enter';
  public static KEYCODE_ESCAPE = 'Escape';

  public static DIGITS_REPRESENTATION_OPTIONS = '1.';

  public static DEFAULT_WIDTH_FOR_MOBILE = 544;
  public static DEFAULT_WIDTH_FOR_DESKTOP = 1200;
  public static SECURITY_PAGES: string[] = [
    'profile',
    'chat',
    'blocked-users',
    'files',
    'posts',
    'group-message',
    'admins',
    'categories',
  ];
}
